import type { LegendChartData, LegendItem, LegendItemConfig } from './types';

/**
 * Extracts legend items from dataset configurations.
 * Only includes datasets that have legendConfig.show = true.
 * Sorts by order field (lower numbers first).
 */
export function extractLegendItems(
    datasetConfigs: Array<{
        label: string;
        legendConfig?: LegendItemConfig;
    }>
): LegendItem[] {
    const items: LegendItem[] = [];

    datasetConfigs.forEach((config, index) => {
        if (config.legendConfig?.show) {
            items.push({
                datasetIndex: index,
                label: config.label,
                color: config.legendConfig.color,
                order: config.legendConfig.order ?? index,
                tooltipKey: config.legendConfig.tooltipKey,
            });
        }
    });

    // Sort by order field
    return items.sort((a, b) => a.order - b.order);
}

/**
 * Filters chart data to only include datasets at the specified visible indices.
 * Returns a new chart data object with filtered datasets.
 *
 * Generic over the chart-data type so callers get their own type back rather
 * than a widened one — line and bar chart data both satisfy `LegendChartData`.
 */
export function filterDatasetsByVisibility<TData extends LegendChartData>(
    data: TData,
    visibleIndices: Set<number>
): TData {
    return {
        ...data,
        datasets: data.datasets.filter((_, index) => visibleIndices.has(index)),
    };
}

/**
 * Filters an array of datasets to only include those at the specified visible indices.
 * Returns a new array with filtered datasets.
 * Works at dataset level before buildChartData.
 */
export function filterDatasetArrayByVisibility<TDataset>(
    datasets: TDataset[],
    visibleIndices: Set<number>
): TDataset[] {
    return datasets.filter((_, index) => visibleIndices.has(index));
}
